import { useState, useLayoutEffect, useCallback } from 'react';

/**
 * Sizes a scroll container to fill the remaining viewport height so that ONLY
 * the table body scrolls — the page itself never overflows, which keeps the
 * mapping toolbar (title / tabs / stat pills / search / filter / edit / save)
 * and the table's sticky column header fixed in place.
 *
 * Returns a callback `ref` to attach to the scrollable container and the
 * computed `maxHeight` string to apply to its style. We use max-height (not a
 * fixed height) so short result sets don't get a big empty box — the container
 * only grows to the viewport limit and scrolls past it.
 *
 * Recomputes on mount, window resize, and any document reflow (e.g. the
 * SelectedFilters chip row appearing/disappearing shifts the container's top).
 *
 * @param bottomGap px to leave between the container bottom and the viewport edge.
 */
export const useScrollableTableHeight = (bottomGap = 24) => {
    const [maxHeight, setMaxHeight] = useState<string>('60vh');
    const [node, setNode] = useState<HTMLDivElement | null>(null);

    const ref = useCallback((n: HTMLDivElement | null) => setNode(n), []);

    useLayoutEffect(() => {
        if (!node) return;

        const recalc = () => {
            const top = node.getBoundingClientRect().top;
            const next = Math.max(200, Math.round(window.innerHeight - top - bottomGap));
            const nextStr = `${next}px`;
            setMaxHeight(prev => (prev === nextStr ? prev : nextStr));
        };

        recalc();
        window.addEventListener('resize', recalc);

        let ro: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            // Fires when layout above the table reflows (filter chips, etc.),
            // which moves the container's top and changes the height it should fill.
            ro = new ResizeObserver(recalc);
            ro.observe(document.body);
        }

        return () => {
            window.removeEventListener('resize', recalc);
            ro?.disconnect();
        };
    }, [node, bottomGap]);

    return { ref, maxHeight };
};
